import React, { useEffect } from 'react'
import { getAttributes, getSizeString, useEventListener } from '../utils/functions';
import { BsCaretDown, BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { TbCopy } from 'react-icons/tb';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { BiMinusCircle } from 'react-icons/bi';

interface Props {
  data: any[]
  setData: React.Dispatch<React.SetStateAction<any[]>>
  selected: string[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  attrKey: string
  attributes?: { name: string, type: string }[]
  displayBooleanAsIcon?: boolean
  isKeyBotton?: boolean
  valueTypes?: { [key: string]: 'byte' | 'date' }
  onClick?: (e: React.MouseEvent<HTMLDivElement>, item: any) => void
}

const Table = ({ data, setData, ...props }: Props) => {

  const [attributes, setAttributes] = React.useState<{ name: string, type: string }[]>([]);
  const [sort, setSort] = React.useState<{ attr: string, order: 'asc' | 'desc' }>({ attr: '', order: 'asc' });
  const [colWidths, setColWidths] = React.useState<{ [key: string]: number }>({});
  const [originalX, setOriginalX] = React.useState(0);
  const [pointerDownAttr, setPointerDownAttr] = React.useState('');

  // setup attributes
  useEffect(() => {
    if (props.attributes) {
      setAttributes(props.attributes);
      return;
    }
    setAttributes(getAttributes(data));
  }, [data, props.attributes]);

  // setup column widths
  useEffect(() => {
    setColWidths(attributes.reduce((acc, attr) => ({ ...acc, [attr.name]: window.innerWidth < 960 ? 100 : 200 }), {}));
  }, [attributes]);

  // filter data
  useEffect(() => {
    if (!data) return;
    let sorted = [...data];
    if (sort.attr) { // sort
      sorted = sorted.sort((a: any, b: any) => {
        if ((a[sort.attr] || '') < (b[sort.attr] || '')) return sort.order === 'asc' ? -1 : 1;
        return sort.order === 'asc' ? 1 : -1;
      })
    }
    setData(sorted);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  // handle resizing
  useEventListener('pointermove', (e: PointerEvent) => {
    if (!pointerDownAttr) return;
    let dist = e.clientX - originalX;
    dist = dist < 0 ? -2 : dist > 0 ? 2 : 0;
    setColWidths(prev => ({ ...prev, [pointerDownAttr]: Math.max(100, prev[pointerDownAttr] + dist) }));
    setOriginalX(e.clientX);
  }, [pointerDownAttr, originalX, colWidths]);

  useEventListener('pointerup', () => {
    setPointerDownAttr('');
    setOriginalX(0);
  }, [pointerDownAttr, originalX, colWidths]);

  // handle pointer down on resizer
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>, attr: string) => {
    setPointerDownAttr(attr);
    setOriginalX(e.clientX);
  }

  return (
    <table className='mt-2 text-xs md:text-sm'>
      <thead className='sticky top-0' style={{ zIndex: 15 }}>
        <tr className='bg-gray-100 *:text-start'>
          <th onClick={() => props.selected.length === data.length ? props.setSelected([]) : props.setSelected(data.map((item: any) => item[props.attrKey]))} className='sticky left-0 z-10 bg-gray-100 min-w-12 p-2 px-2'>
            <button className={`w-4 h-4 flex items-center justify-center border border-black/20 text-xs rounded ${props.selected.length === data.length && data.length !== 0 ? 'bg-primary border-primary' : 'bg-white'}`}><i className='fa-solid fa-check text-white' /></button>
            <div className='absolute h-full top-0 -right-1.5 p-1.5'><div className='h-full border-l border-black/10' /></div>
          </th>
          {attributes.map((attr: { name: string, type: string }) => (
            <>
              <th className='relative p-2 px-3 overflow-hidden text-ellipsis whitespace-nowrap' style={{ minWidth: colWidths[attr.name], maxWidth: colWidths[attr.name] }}>
                <div>{attr.name}</div>
                {sort.attr === attr.name ? (
                  sort.order === 'asc' ? (
                    <BsCaretUpFill onClick={() => setSort({ attr: attr.name, order: sort.attr === attr.name ? sort.order === 'asc' ? 'desc' : 'asc' : 'asc' })} className='text-xl absolute top-1/2 right-2 -translate-y-1/2 p-1 bg-gray-100 cursor-pointer' />
                  ) : (
                    <BsCaretDownFill onClick={() => setSort({ attr: attr.name, order: sort.attr === attr.name ? sort.order === 'asc' ? 'desc' : 'asc' : 'asc' })} className='text-xl absolute top-1/2 right-2 -translate-y-1/2 p-1 bg-gray-100 cursor-pointer' />
                  )
                ) : (
                  <BsCaretDown onClick={() => setSort({ attr: attr.name, order: sort.attr === attr.name ? sort.order === 'asc' ? 'desc' : 'asc' : 'asc' })} className='text-xl absolute top-1/2 right-2 -translate-y-1/2 p-1 bg-gray-100 cursor-pointer' />
                )}
                <div id={`resizer-${attr}`} className='absolute h-full top-0 -right-1.5 p-1.5 cursor-ew-resize' onPointerDown={(e) => handlePointerDown(e, attr.name)}><div className='h-full w-0.5 rounded bg-gray-300 border-black/20' /></div>
              </th>
            </>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((item: any) => (
          <tr className='border-b'>
            <td onClick={() => props.setSelected((prev) => prev.includes(item[props.attrKey]) ? prev.filter(k => k !== item[props.attrKey]) : [...prev, item[props.attrKey]])} className='sticky z-10 left-0 min-w-12 p-1 px-2 bg-white'>
              <button className={`w-4 h-4 flex items-center justify-center border border-black/20 text-xs rounded ${props.selected.includes(item[props.attrKey]) ? 'bg-primary border-primary' : 'bg-white'}`}><i className='fa-solid fa-check text-white' /></button>
            </td>
            {attributes.map((attr: { name: string, type: string }) => (
              <>
                <td onClick={(e) => {
                  if (!props.isKeyBotton || attr.name !== props.attrKey || (e.target as HTMLElement).closest('.copy-button')) return;
                  props.onClick?.(e, item);
                }} className={`${props.isKeyBotton && attr.name === props.attrKey ? 'key-attr cursor-pointer hover:underline hover:text-primary' : ''} relative p-2 px-3 overflow-hidden text-ellipsis whitespace-nowrap [&>button]:hover:opacity-100`} style={{ minWidth: colWidths[attr.name], maxWidth: colWidths[attr.name] }}>
                  {/* display boolean icon */}
                  {props.displayBooleanAsIcon && attr.type === 'boolean' ? (
                    item[attr.name] ? <IoMdCheckmarkCircleOutline className='text-lg text-emerald-500' /> : <BiMinusCircle className='text-lg text-red-400' />
                  // display as specific type
                  ) : props.valueTypes?.[attr.name] === 'byte' ? (
                    getSizeString(item[attr.name])
                  // default
                  ) : (['number', 'string'].includes(attr.type) ? item[attr.name] : (JSON.stringify(item[attr.name])))}
                  {item[attr.name] !== undefined && <button onClick={() => navigator.clipboard.writeText(attr.type === 'string' ? item[attr.name] : JSON.stringify(item[attr.name]))} className='copy-button opacity-0 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary bg-white h-6 w-6 flex items-center justify-center rounded'><TbCopy /></button>}
                </td>
              </>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default Table